import { useLocation, useNavigate } from 'react-router';
import { getVideoManagerPath, resolveBackgroundLocation } from '../utils';
import { useVideoManagerContext } from '../context/VideoManagerContext';

/**
 * Opens the video manager modal with the given video id and page, keeping the background page.
 *
 * - Called from a regular page (outside the modal): the current location becomes the background page.
 * - Called from inside the modal (e.g. switching tabs): the existing background page is forwarded.
 *
 * @param advertiserPrettyIdOverride - Required when called outside the modal (no VideoManagerProvider there).
 * @example
 * const openVideoManager = useOpenVideoManagerModal(advertiser._id);
 * <button onClick={() => openVideoManager('add')}>Upload</button>
 */
export function useOpenVideoManagerModal(advertiserPrettyIdOverride?: string) {
  const navigate = useNavigate();
  const location = useLocation();
  const { advertiserPrettyId: contextAdvertiserPrettyId } = useVideoManagerContext();

  const advertiserPrettyId = advertiserPrettyIdOverride ?? contextAdvertiserPrettyId;
  // Inside the modal keep the original background; outside, the current page *is* the background.
  const isInsideModal = location.pathname.startsWith('/video-manager/');
  // A modal URL is never used as its own background (avoids rendering the modal twice).
  const backgroundLocation = resolveBackgroundLocation(location) ?? (isInsideModal ? undefined : location);

  return (page: 'library' | 'add', videoId?: string, advertiserPrettyIdForCall?: string) => {
    void navigate(
      getVideoManagerPath(advertiserPrettyIdForCall ?? advertiserPrettyId, page, videoId, backgroundLocation),
      {
        state: backgroundLocation ? { backgroundLocation } : undefined,
      },
    );
  };
}
